import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError } from '../../../lib/api'
import { activateInvitation, validateInvitationToken } from '../api'
import { AuthAlert } from '../components/AuthAlert'
import { AuthFormIntro } from '../components/AuthFormIntro'
import { Button } from '@/components/ui/button'
import { AuthSplitShell } from '../components/AuthSplitShell'
import { AuthPasswordField } from '../components/AuthPasswordField'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { CheckCircle, Mail, MailX } from 'lucide-react'
import { getPasswordStrength } from '@/lib/password-strength'
import { cn } from '@/lib/utils'

export function InvitationActivationPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const strength = getPasswordStrength(password)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [displayName, setDisplayName] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activated, setActivated] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  const validateMutation = useMutation({
    mutationFn: validateInvitationToken,
    onSuccess: (response) => {
      setTokenValid(response.data.valid)
      if (!response.data.valid) {
        setErrorMessage(response.data.message)
      } else {
        setDisplayName(response.data.display_name ?? null)
        setErrorMessage(null)
      }
    },
    onError: (error) => {
      const apiError = error as ApiError
      if (apiError.status === 419 || apiError.code === 'SESSION_EXPIRED') {
        setTokenValid(false)
        setErrorMessage('SESSION_EXPIRED')
      } else {
        setTokenValid(false)
        setErrorMessage(apiError.message)
      }
    },
  })

  const activateMutation = useMutation({
    mutationFn: activateInvitation,
    onSuccess: () => {
      setActivated(true)
    },
    onError: (error) => {
      const apiError = error as ApiError
      if (apiError.status === 419 || apiError.code === 'SESSION_EXPIRED') {
        setErrorMessage('SESSION_EXPIRED')
      } else {
        setErrorMessage(apiError.message)
      }
    },
  })

  useEffect(() => {
    if (email.trim() !== '' && token.trim() !== '') {
      validateMutation.mutate({ email: email.trim(), token: token.trim() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (activated) {
    return (
      <AuthSplitShell>
        <div className="w-full space-y-5">
              <div className="flex flex-col gap-4 py-4 text-left">
                <div className="flex size-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30">
                  <CheckCircle className="size-7" />
                </div>
                <AuthFormIntro
                  as="h2"
                  title={t('auth.invitation.successTitle')}
                  description={t('auth.invitation.successDescription')}
                  className="space-y-1.5"
                  titleClassName="text-2xl"
                />
              </div>

              <div className="rounded-xl border border-border bg-muted/30 px-4 py-4">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{t('auth.invitation.checkInbox')}</p>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {t('auth.invitation.checkInboxDescription', { email })}
                    </p>
                  </div>
                </div>
              </div>

              <Button asChild variant="outline" className="w-full">
                <Link to="/login">{t('auth.invitation.backToLogin')}</Link>
              </Button>

              <p className="text-left text-xs leading-5 text-muted-foreground">
                {t('auth.invitation.noEmailYet')}{' '}
                <Link to={`/verify-email?email=${encodeURIComponent(email)}`} className="font-medium text-foreground transition hover:text-foreground/80">
                  {t('auth.invitation.resendVerification')}
                </Link>
              </p>
        </div>
      </AuthSplitShell>
    )
  }

  return (
    <AuthSplitShell>
      <div className="w-full space-y-5">
            <AuthFormIntro
              title={
                displayName
                  ? t('auth.invitation.welcomeTitle', { name: displayName })
                  : t('auth.invitation.title')
              }
              description={t('auth.invitation.description')}
            />

            {tokenValid === false ? (
              errorMessage === 'INVITATION_CANCELLED' ? (
                <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
                  <div className="flex size-16 items-center justify-center rounded-full bg-rose-50 text-rose-500 dark:bg-rose-950/30">
                    <MailX className="size-8" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-semibold">{t('auth.invitation.cancelledTitle')}</h2>
                    <p className="text-sm text-muted-foreground">{t('auth.invitation.cancelledDescription')}</p>
                  </div>
                  <Button asChild variant="outline" className="mt-2">
                    <Link to="/login">{t('auth.invitation.backToLogin')}</Link>
                  </Button>
                </div>
              ) : (
                <AuthAlert
                  variant="error"
                  action={
                    errorMessage === 'SESSION_EXPIRED' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg"
                        onClick={() => window.location.reload()}
                      >
                        {t('auth.invitation.refreshPage')}
                      </Button>
                    ) : (
                      <Button asChild variant="outline" size="sm" className="rounded-lg">
                        <Link to="/login">{t('auth.invitation.backToLogin')}</Link>
                      </Button>
                    )
                  }
                >
                  {errorMessage === 'SESSION_EXPIRED'
                    ? t('auth.invitation.sessionExpiredError')
                    : (errorMessage ?? t('auth.invitation.invalidLink'))}
                </AuthAlert>
              )
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  setErrorMessage(null)

                  if (password !== passwordConfirmation) {
                    setErrorMessage(t('auth.errors.passwordMismatch'))
                    return
                  }

                  if (!termsAccepted) {
                    setErrorMessage(t('auth.invitation.termsRequired'))
                    return
                  }

                  activateMutation.mutate({
                    email: email.trim(),
                    token: token.trim(),
                    password,
                    password_confirmation: passwordConfirmation,
                    terms_accepted: true,
                  })
                }}
              >
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="email">
                    {t('auth.email')}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    disabled
                    className="px-4 opacity-60"
                  />
                </div>

                <div className="space-y-2">
                  <AuthPasswordField
                    id="password"
                    autoComplete="new-password"
                    placeholder={t('auth.invitation.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    label={t('auth.invitation.passwordLabel')}
                    required
                    minLength={8}
                  />
                  {password.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                          <div
                            className={cn('h-full transition-all duration-300', strength.color)}
                            style={{ width: `${(strength.score / 5) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {t(`settings.dialogs.password.strength.${strength.label}` as const)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('settings.dialogs.password.strengthLabel')}</p>
                    </div>
                  )}
                </div>

                <AuthPasswordField
                  id="password-confirm"
                  autoComplete="new-password"
                  placeholder={t('auth.invitation.passwordConfirmPlaceholder')}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  label={t('auth.invitation.passwordConfirmLabel')}
                  required
                />
                {passwordConfirmation.length > 0 && password !== passwordConfirmation && (
                  <p className="text-xs text-destructive">{t('auth.errors.passwordMismatch')}</p>
                )}

                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => {
                        setTermsAccepted(checked === true)
                      }}
                    />
                    <label htmlFor="terms" className="text-sm leading-5 cursor-pointer">
                      {t('auth.invitation.iAccept')}{' '}
                      <a
                        href="/legal/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:text-primary/80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('auth.invitation.termsLink')}
                      </a>
                      {', '}
                      <a
                        href="/legal/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:text-primary/80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('auth.invitation.privacyLink')}
                      </a>
                      {t('auth.invitation.and')}{' '}
                      <a
                        href="/legal/cookies"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:text-primary/80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('auth.invitation.cookiesLink')}
                      </a>
                      {'.'}
                    </label>
                  </div>
                </div>

                {errorMessage ? (
                  <AuthAlert
                    variant="error"
                    action={
                      errorMessage === 'SESSION_EXPIRED' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg"
                          onClick={() => window.location.reload()}
                        >
                          {t('auth.invitation.refreshPage')}
                        </Button>
                      ) : null
                    }
                  >
                    {errorMessage === 'SESSION_EXPIRED'
                      ? t('auth.invitation.sessionExpiredError')
                      : errorMessage}
                  </AuthAlert>
                ) : null}

                <Button
                  size="lg"
                  type="submit"
                  disabled={!termsAccepted || activateMutation.isPending || tokenValid !== true || validateMutation.isPending}
                  className="w-full"
                >
                  {activateMutation.isPending
                    ? t('auth.invitation.creatingAccount')
                    : t('auth.invitation.submit')}
                </Button>
              </form>
            )}

            <div className="flex justify-center pt-2">
              <Link
                to="/login"
                className="text-sm font-medium text-foreground underline underline-offset-4 transition hover:text-foreground/80"
              >
                {t('auth.invitation.backToLogin')}
              </Link>
            </div>
      </div>
    </AuthSplitShell>
  )
}
